import { TechniqueBreakdown } from "../techniqueBreakdowns";

export const problemSolvingTutorials: Record<string, TechniqueBreakdown> = {
  "Find a Pattern": {
    steps: [
      {
        title: "Step 1: Generate Data",
        explanation:
          "You can't find a pattern in your head. Start by calculating the first few simple versions of the problem. Write them down! For example, if finding the last digit of 3^100, start with 3^1, 3^2, 3^3, 3^4, etc.",
        practice: {
          problem: "Find the last digit of 7^123",
          question:
            "Calculate the first 5 powers of 7 and write down their last digits only",
          answer: "7, 9, 3, 1, 7",
          hint: "7^1=7, 7^2=49 (last digit 9), 7^3=343 (last digit 3), 7^4=2401 (last digit 1), 7^5=16807 (last digit 7)",
          intermediateSteps: [
            { label: "7^1", answer: "7", hint: "Last digit of 7 is 7" },
            { label: "7^2 = 49", answer: "9", hint: "Last digit of 49 is 9" },
            { label: "7^3 = 343", answer: "3", hint: "Last digit of 343 is 3" },
            {
              label: "7^4 = 2401",
              answer: "1",
              hint: "Last digit of 2401 is 1",
            },
            { label: "7^5", answer: "7", hint: "Last digit repeats: 7" },
          ],
        },
      },
      {
        title: "Step 2: Organize Your Data",
        explanation:
          "Don't let your numbers float around. Put them in a clear, organized list or table. This makes it easy to see how one number connects to the next.",
        practice: {
          problem: "Last digits of powers of 7",
          question: "Write the sequence of last digits you found in order",
          answer: "7, 9, 3, 1",
          hint: "List them in order: 7,  9, 3, 1 (starts repeating)",
        },
      },
      {
        title: "Step 3: Analyze the Data",
        explanation:
          "Look at your organized list and ask: Is it a repeating cycle? Is it growing? How does it grow? Are the differences between terms a pattern?",
        practice: {
          problem: "Sequence: 7, 9, 3, 1, 7, 9...",
          question: "What type of pattern do you see?",
          answer: "repeating cycle",
          hint: "The sequence repeats: 7, 9, 3, 1, then back to 7, 9, 3, 1... This is a cycle!",
          intermediateSteps: [
            {
              label: "Cycle length",
              answer: "4",
              hint: "The pattern repeats every 4 terms",
            },
            {
              label: "Pattern",
              answer: "7, 9, 3, 1",
              hint: "These 4 digits repeat forever",
            },
          ],
        },
      },
      {
        title: "Step 4: Form a Hypothesis",
        explanation:
          "Based on your analysis, make an educated guess about the rule. For cyclic patterns, identify the cycle length. For growing patterns, find the formula.",
        practice: {
          problem: "Last digit pattern of powers of 7",
          question: "What is your hypothesis about this pattern?",
          answer: "4-term repeating cycle",
          hint: "The last digits repeat in a cycle of 4: position 1→7, position 2→9, position 3→3, position 4→1 - 4-term repeating cycle",
        },
      },
      {
        title: "Step 5: Test, Confirm, and Solve",
        explanation:
          "Use your rule to predict the answer. For cyclic patterns, find the remainder when dividing the position by the cycle length. The remainder tells you which term in the cycle to use.",
        practice: {
          problem: "What is the last digit of 7^123?",
          question: "Using your pattern, find: 123 ÷ 4 = ? remainder ?",
          answer: "30 remainder 3",
          hint: "123 ÷ 4 = 30 with remainder 3",
          intermediateSteps: [
            { label: "123 ÷ 4", answer: "30 R 3", hint: "123 = 4 × 30 + 3" },
            {
              label: "Remainder 3 means",
              answer: "3rd term in pattern",
              hint: "Remainder tells us position in cycle",
            },
            {
              label: "3rd term is",
              answer: "3",
              hint: "Looking at pattern 7,9,3,1 → 3rd is 3",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "What is the last digit of 7^123?",
      fullSolution:
        "3 (The pattern 7,9,3,1 repeats every 4 terms. 123÷4 has remainder 3, so the answer is the 3rd term: 3)",
    },
  },

  "Find a Pattern - Arithmetic": {
    steps: [
      {
        title: "Step 1: Generate Data",
        explanation:
          "Write down the first few terms that are given in the sequence. Make sure you have at least 4-5 terms to spot the pattern clearly.",
        practice: {
          problem: "A sequence starts: 5, 9, 13, 17...",
          question: "Write down the first 4 terms",
          answer: "5, 9, 13, 17",
          hint: "The sequence is: 5, 9, 13, 17",
        },
      },
      {
        title: "Step 2: Find the Differences",
        explanation:
          "Subtract each term from the next one. If the differences are all the same, you have an arithmetic (linear) pattern!",
        practice: {
          problem: "Sequence: 5, 9, 13, 17",
          question: "What is 9 - 5? What is 13 - 9? What is 17 - 13?",
          answer: "4, 4, 4",
          hint: "Each difference is 4. This is a constant difference!",
          intermediateSteps: [
            {
              label: "9 - 5",
              answer: "4",
              hint: "Second term minus first term",
            },
            {
              label: "13 - 9",
              answer: "4",
              hint: "Third term minus second term",
            },
            {
              label: "17 - 13",
              answer: "4",
              hint: "Fourth term minus third term",
            },
          ],
        },
      },
      {
        title: "Step 3: Form the Rule",
        explanation:
          'For arithmetic sequences, the rule is: First term + (difference × number of jumps). The "jumps" is always (n - 1) where n is the position.',
        practice: {
          problem: "Pattern: Start at 5, add 4 each time",
          question: "What is the formula for the nth term?",
          answer: "5 + 4(n-1)",
          hint: "First term is 5. We add 4 each time. Formula: 5 + 4(n-1)",
        },
      },
      {
        title: "Step 4: Test Your Rule",
        explanation:
          "Verify your rule works by testing it on the given terms. For example, when n=2, does it give the 2nd term?",
        practice: {
          problem: "Rule: 5 + 4(n-1)",
          question: "Test with n=3. What do you get?",
          answer: "13",
          hint: "5 + 4(3-1) = 5 + 4(2) = 5 + 8 = 13 ✓",
          intermediateSteps: [
            { label: "n = 3", answer: "3", hint: "We want the 3rd term" },
            { label: "3 - 1", answer: "2", hint: "Number of jumps is n-1" },
            {
              label: "4 × 2",
              answer: "8",
              hint: "Multiply difference by jumps",
            },
            {
              label: "5 + 8",
              answer: "13",
              hint: "Add to first term. Matches!",
            },
          ],
        },
      },
      {
        title: "Step 5: Solve for Large n",
        explanation:
          "Now use your rule to find any term in the sequence, even very large ones!",
        practice: {
          problem: "Find the 30th term using rule: 5 + 4(n-1)",
          question: "What is 5 + 4(30-1)?",
          answer: "121",
          hint: "5 + 4(29) = 5 + 116 = 121",
          intermediateSteps: [
            {
              label: "30 - 1",
              answer: "29",
              hint: "Number of jumps for 30th term",
            },
            { label: "4 × 29", answer: "116", hint: "Total distance traveled" },
            { label: "5 + 116", answer: "121", hint: "The 30th term is 121" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "A sequence starts: 5, 9, 13, 17... What is the 30th term?",
      fullSolution:
        "121 (Pattern: add 4. Rule: 5 + 4(n-1). For n=30: 5 + 4(29) = 121)",
    },
  },

  "Find a Pattern - Geometric": {
    steps: [
      {
        title: "Step 1: Generate Data",
        explanation:
          "Write down the first few terms. For geometric patterns, the sequence grows by multiplication, not addition.",
        practice: {
          problem:
            "A plant's height: Week 1: 3 cm, Week 2: 6 cm, Week 3: 12 cm",
          question: "Write down the first 3 terms",
          answer: "3, 6, 12",
          hint: "The heights are: 3 cm, 6 cm, 12 cm",
        },
      },
      {
        title: "Step 2: Find the Ratios",
        explanation:
          "Instead of subtracting, DIVIDE each term by the previous one. If the ratios are all the same, you have a geometric (exponential) pattern!",
        practice: {
          problem: "Sequence: 3, 6, 12",
          question: "What is 6 ÷ 3? What is 12 ÷ 6?",
          answer: "2, 2",
          hint: "Each term is multiplied by 2!",
          intermediateSteps: [
            { label: "6 ÷ 3", answer: "2", hint: "Second ÷ First = 2" },
            { label: "12 ÷ 6", answer: "2", hint: "Third ÷ Second = 2" },
          ],
        },
      },
      {
        title: "Step 3: Form the Rule",
        explanation:
          "For geometric sequences, the rule is: First term × (ratio)^(n-1). The exponent is always (n-1).",
        practice: {
          problem: "Pattern: Start at 3, multiply by 2 each time",
          question: "What is the formula for the nth term?",
          answer: "3 × 2^(n-1)",
          hint: "First term is 3. Ratio is 2. Formula: 3 × 2^(n-1)",
        },
      },
      {
        title: "Step 4: Test Your Rule",
        explanation:
          "Verify by testing on a given term. Remember: exponents come before multiplication!",
        practice: {
          problem: "Rule: 3 × 2^(n-1)",
          question: "Test with n=3. What do you get?",
          answer: "12",
          hint: "3 × 2^(3-1) = 3 × 2^2 = 3 × 4 = 12 ✓",
          intermediateSteps: [
            { label: "n = 3", answer: "3", hint: "We want the 3rd term" },
            { label: "3 - 1", answer: "2", hint: "The exponent is n-1" },
            { label: "2^2", answer: "4", hint: "2 to the power of 2" },
            {
              label: "3 × 4",
              answer: "12",
              hint: "Multiply by first term. Matches!",
            },
          ],
        },
      },
      {
        title: "Step 5: Solve for Large n",
        explanation:
          "Use your rule to find any term. For large exponents, calculate step by step or use a calculator.",
        practice: {
          problem: "Find the 10th week using rule: 3 × 2^(n-1)",
          question: "What is 3 × 2^9?",
          answer: "1536",
          hint: "2^9 = 512, then 3 × 512 = 1536",
          intermediateSteps: [
            { label: "10 - 1", answer: "9", hint: "Exponent for 10th term" },
            { label: "2^9", answer: "512", hint: "2×2×2×2×2×2×2×2×2 = 512" },
            {
              label: "3 × 512",
              answer: "1536",
              hint: "Week 10: 1536 cm tall!",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem:
        "A plant doubles its height every week. If it is 3 cm in Week 1, how tall in Week 10?",
      fullSolution:
        "1536 cm (Pattern: multiply by 2. Rule: 3 × 2^(n-1). For n=10: 3 × 2^9 = 3 × 512 = 1536)",
    },
  },

  "Find a Pattern - Quadratic": {
    steps: [
      {
        title: "Step 1: Generate Data",
        explanation:
          "Write down the first few terms. For quadratic patterns (like square numbers), the sequence grows faster than simple addition.",
        practice: {
          problem:
            "Tower blocks: Tower 1: 1 block, Tower 2: 4 blocks, Tower 3: 9 blocks, Tower 4: 16 blocks",
          question: "Write down the first 4 terms",
          answer: "1, 4, 9, 16",
          hint: "The sequence is: 1, 4, 9, 16 blocks",
        },
      },
      {
        title: "Step 2: Find First Differences",
        explanation:
          "Subtract consecutive terms. For quadratic patterns, these differences won't be constant, but they'll form a pattern themselves!",
        practice: {
          problem: "Sequence: 1, 4, 9, 16",
          question: "What are the differences? (4-1), (9-4), (16-9)",
          answer: "3, 5, 7",
          hint: "The differences are: 3, 5, 7 (they're growing!)",
          intermediateSteps: [
            { label: "4 - 1", answer: "3", hint: "First difference" },
            { label: "9 - 4", answer: "5", hint: "Second difference" },
            { label: "16 - 9", answer: "7", hint: "Third difference" },
          ],
        },
      },
      {
        title: "Step 3: Find Second Differences",
        explanation:
          "Now find the differences OF the differences! If THESE are constant, you have a quadratic pattern.",
        practice: {
          problem: "First differences: 3, 5, 7",
          question: "What are the second differences? (5-3), (7-5)",
          answer: "2, 2",
          hint: "Second differences are constant = 2. Quadratic pattern confirmed!",
          intermediateSteps: [
            { label: "5 - 3", answer: "2", hint: "Difference of differences" },
            {
              label: "7 - 5",
              answer: "2",
              hint: "Constant! This is quadratic",
            },
          ],
        },
      },
      {
        title: "Step 4: Recognize the Pattern",
        explanation:
          "Look at the original sequence. Do the numbers look familiar? 1, 4, 9, 16 are perfect squares: 1², 2², 3², 4²!",
        practice: {
          problem: "Sequence: 1, 4, 9, 16",
          question: "What is the pattern? What is each term equal to?",
          answer: "n²",
          hint: "1=1², 4=2², 9=3², 16=4². The rule is n²!",
        },
      },
      {
        title: "Step 5: Solve for Large n",
        explanation:
          "Use your rule to find any term. For square numbers, just square the position number!",
        practice: {
          problem: "Find Tower 25 using rule: n²",
          question: "What is 25²?",
          answer: "625",
          hint: "25 × 25 = 625 blocks",
          intermediateSteps: [
            { label: "n = 25", answer: "25", hint: "We want the 25th tower" },
            { label: "25²", answer: "625", hint: "25 × 25 = 625 blocks!" },
          ],
        },
      },
    ],
    finalExample: {
      problem:
        "A builder makes towers. Tower 1: 1 block, Tower 2: 4 blocks, Tower 3: 9 blocks, Tower 4: 16 blocks. How many blocks in Tower 25?",
      fullSolution:
        "625 blocks (Pattern: n². Towers have 1², 2², 3², 4²... blocks. Tower 25: 25² = 625)",
    },
  },

  "1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d": {
    title: "Work Backwards",
    description:
      "Learn to find a starting number when you only know the final answer by using inverse operations.",
    steps: [
      {
        stepNumber: 1,
        title: 'Find Your New "Start"',
        explanation:
          "Read the problem and identify the final number (the end result). This becomes your starting point for working backwards.",
        tips: [
          'Look for phrases like "I am left with", "the result is", "I get", "ends up with"',
          "The final number is where your backwards journey begins",
          "Circle or highlight the final number",
        ],
        examples: {
          simple: {
            problem:
              "I think of a number, add 5, and get 12. What was my number?",
            solution: "12",
            explanation:
              "The final number is 12. This is our starting point for working backwards.",
          },
          intermediate: {
            problem:
              "Sarah starts with some money, spends $15, then spends $20, and has $25 left. How much did she start with?",
            solution: "$25",
            explanation:
              "The final amount is $25. We will work backwards from here.",
            intermediateSteps: [
              {
                label: "Final amount",
                answer: "$25",
                hint: "This is where we start working backwards",
              },
            ],
          },
        },
      },
      {
        stepNumber: 2,
        title: "List the Steps (Forward)",
        explanation:
          "Read the problem from beginning to end and write down every operation that happens, in the exact order it happens.",
        tips: [
          "Write operations in order: Step 1, Step 2, Step 3...",
          'Be specific: "add 5", "multiply by 3", "divide by 2"',
          'Create a "Forward Steps" column to stay organized',
        ],
        examples: {
          simple: {
            problem:
              "I think of a number, add 5, and get 12. What was my number?",
            solution: "add 5",
            explanation: "Only one operation: add 5",
          },
          intermediate: {
            problem:
              "I multiply a number by 3, then add 8, then divide by 2, and get 10. What was my number?",
            solution:
              "Step 1: multiply by 3, Step 2: add 8, Step 3: divide by 2",
            explanation: "Three operations in order",
            intermediateSteps: [
              {
                label: "Step 1",
                answer: "multiply by 3",
                hint: "First operation",
              },
              { label: "Step 2", answer: "add 8", hint: "Second operation" },
              {
                label: "Step 3",
                answer: "divide by 2",
                hint: "Final operation",
              },
            ],
          },
        },
      },
      {
        stepNumber: 3,
        title: "Reverse and Invert!",
        explanation:
          "Start at the final number and work UP your list (from bottom to top), doing the OPPOSITE (inverse) of each step. Addition ↔ Subtraction, Multiplication ↔ Division, Doubling ↔ Halving.",
        tips: [
          "Go backwards through the steps (last step first)",
          "Use inverse operations: add→subtract, multiply→divide, double→halve",
          "Keep track of your calculations at each step",
          "The final result is your starting number!",
        ],
        practice: {
          problem:
            "I multiply a number by 3, add 8, divide by 2, and get 10. What was my number?",
          question: "Work backwards from 10. Fill in each step:",
          answer: "4",
          hint: "Work backwards: Step 3→Step 2→Step 1. Use inverse operations.",
          intermediateSteps: [
            {
              label: "Start with final",
              answer: "10",
              hint: "The result we got",
            },
            {
              label: "Undo divide by 2",
              answer: "20",
              hint: "Inverse is multiply by 2: 10 × 2 = 20",
            },
            {
              label: "Undo add 8",
              answer: "12",
              hint: "Inverse is subtract 8: 20 - 8 = 12",
            },
            {
              label: "Undo multiply by 3",
              answer: "4",
              hint: "Inverse is divide by 3: 12 ÷ 3 = 4",
            },
          ],
        },
        examples: {
          simple: {
            problem:
              "I think of a number, add 5, and get 12. What was my number?",
            solution: "7",
            explanation:
              'Start with 12. Inverse of "add 5" is "subtract 5". 12 - 5 = 7',
            intermediateSteps: [
              { label: "Start", answer: "12", hint: "The final number" },
              { label: "Undo add 5", answer: "12 - 5 = 7", hint: "Subtract 5" },
              { label: "Answer", answer: "7", hint: "The starting number!" },
            ],
          },
          intermediate: {
            problem:
              "I multiply a number by 3, add 8, divide by 2, and get 10. What was my number?",
            solution: "4",
            explanation:
              "Work backwards from 10: Undo divide by 2 → 10 × 2 = 20. Undo add 8 → 20 - 8 = 12. Undo multiply by 3 → 12 ÷ 3 = 4",
            intermediateSteps: [
              {
                label: "Start with final",
                answer: "10",
                hint: "The result we got",
              },
              {
                label: "Undo Step 3",
                answer: "10 × 2 = 20",
                hint: "Inverse of divide by 2 is multiply by 2",
              },
              {
                label: "Undo Step 2",
                answer: "20 - 8 = 12",
                hint: "Inverse of add 8 is subtract 8",
              },
              {
                label: "Undo Step 1",
                answer: "12 ÷ 3 = 4",
                hint: "Inverse of multiply by 3 is divide by 3",
              },
              {
                label: "Answer",
                answer: "4",
                hint: "The starting number was 4!",
              },
            ],
          },
        },
      },
      {
        stepNumber: 4,
        title: "Check Your Answer (The Golden Rule)",
        explanation:
          "Take your answer and run it FORWARD through the original problem. If you land on the correct final number, you are 100% correct!",
        tips: [
          "Start with your answer",
          "Apply each operation in the original order",
          "If you get the final number, you're correct!",
          "If not, check your inverse operations",
        ],
        examples: {
          simple: {
            problem:
              "I think of a number, add 5, and get 12. Check if 7 is correct.",
            solution: "Correct!",
            explanation: "Start with 7: 7 + 5 = 12 ✓",
            intermediateSteps: [
              { label: "Start", answer: "7", hint: "Our answer" },
              {
                label: "Add 5",
                answer: "7 + 5 = 12",
                hint: "Apply the original operation",
              },
              {
                label: "Result",
                answer: "12",
                hint: "Matches the final number! ✓",
              },
            ],
          },
          intermediate: {
            problem:
              "I multiply by 3, add 8, divide by 2, and get 10. Check if 4 is correct.",
            solution: "Correct!",
            explanation:
              "Start with 4: 4 × 3 = 12, then 12 + 8 = 20, then 20 ÷ 2 = 10 ✓",
            intermediateSteps: [
              { label: "Start", answer: "4", hint: "Our answer" },
              {
                label: "Multiply by 3",
                answer: "4 × 3 = 12",
                hint: "First operation",
              },
              {
                label: "Add 8",
                answer: "12 + 8 = 20",
                hint: "Second operation",
              },
              {
                label: "Divide by 2",
                answer: "20 ÷ 2 = 10",
                hint: "Final operation",
              },
              {
                label: "Result",
                answer: "10",
                hint: "Matches! We are correct ✓",
              },
            ],
          },
        },
      },
      {
        stepNumber: 5,
        title: "Master the Inverse Operations",
        explanation:
          "Remember the pairs of inverse operations that undo each other. This is the key to working backwards!",
        tips: [
          "Addition ↔ Subtraction (add 5 undoes subtract 5)",
          "Multiplication ↔ Division (multiply by 3 undoes divide by 3)",
          "Doubling ↔ Halving (double undoes halve)",
          "Squaring ↔ Square Root (square undoes square root)",
          'The trickiest: "spent half" means you DOUBLE to go backwards',
        ],
        practice: {
          problem:
            "Sarah has some money. She spends half, then spends $10, and has $15 left. How much did she start with?",
          question:
            'Work backwards from $15. Remember: inverse of "spend half" is DOUBLE!',
          answer: "$50",
          hint: "First undo spending $10, then undo spending half by doubling.",
          intermediateSteps: [
            { label: "Start with final", answer: "$15", hint: "Amount left" },
            {
              label: "Undo spend $10",
              answer: "$25",
              hint: "Add back $10: $15 + $10 = $25",
            },
            {
              label: "Undo spend half",
              answer: "$50",
              hint: "Double it (inverse of halving): $25 × 2 = $50",
            },
          ],
        },
        examples: {
          simple: {
            problem:
              "Sarah has some money. She spends half, then spends $10, and has $15 left. How much did she start with?",
            solution: "$50",
            explanation:
              "Work backwards from $15: Undo spend $10 → 15 + 10 = 25. Undo spend half → 25 × 2 = 50",
            intermediateSteps: [
              { label: "Start", answer: "$15", hint: "Amount left" },
              {
                label: "Undo spend $10",
                answer: "$15 + $10 = $25",
                hint: "Add back the $10",
              },
              {
                label: "Undo spend half",
                answer: "$25 × 2 = $50",
                hint: "Double it (inverse of halving)",
              },
              { label: "Answer", answer: "$50", hint: "She started with $50!" },
            ],
          },
          intermediate: {
            problem:
              "Tom thinks of a number, doubles it, subtracts 6, and gets 18. What was his number?",
            solution: "12",
            explanation:
              "Work backwards from 18: Undo subtract 6 → 18 + 6 = 24. Undo double → 24 ÷ 2 = 12",
            intermediateSteps: [
              { label: "Start", answer: "18", hint: "The result" },
              {
                label: "Undo subtract 6",
                answer: "18 + 6 = 24",
                hint: "Add 6 back",
              },
              {
                label: "Undo double",
                answer: "24 ÷ 2 = 12",
                hint: "Halve it (inverse of doubling)",
              },
              {
                label: "Answer",
                answer: "12",
                hint: "The starting number was 12!",
              },
            ],
          },
        },
      },
    ],
    finalExample: {
      problem:
        "Emma thinks of a number, multiplies it by 4, adds 12, divides by 2, and gets 30. What number did Emma start with?",
      fullSolution:
        "12 (Work backwards: 30 × 2 = 60, then 60 - 12 = 48, then 48 ÷ 4 = 12. Check: 12 × 4 = 48, 48 + 12 = 60, 60 ÷ 2 = 30 ✓)",
    },
  },

  "2b3c4d5e-6f7a-8b9c-0d1e-2f3a4b5c6d7e": {
    title: "Solve a Simpler Problem",
    description:
      "Break down huge, scary problems by solving tiny versions first to find the pattern.",
    steps: [
      {
        stepNumber: 1,
        title: 'Identify the "Scary Number"',
        explanation:
          "Read the problem and find the number that makes it seem impossible. Usually a big number like 100 people, 1000-story tower, or 50×50 grid.",
        tips: [
          "Look for large numbers that seem overwhelming",
          "The scary number is often what the question is asking about",
          'Examples: "50 people", "100 floors", "1000 handshakes"',
        ],
        examples: {
          simple: {
            problem:
              "In a room with 50 people, everyone shakes hands once. How many handshakes?",
            solution: "50",
            explanation:
              "The scary number is 50. Too many people to count or draw individually!",
          },
          intermediate: {
            problem:
              "A tower has 100 floors. Each floor has 3 more blocks than the one below. Floor 1 has 5 blocks. Total blocks?",
            solution: "100 and the pattern (3 more each floor)",
            explanation:
              "Two scary parts: 100 floors (too many to count) and the growing pattern.",
            intermediateSteps: [
              {
                label: "Scary number",
                answer: "100 floors",
                hint: "Way too many to draw or count manually",
              },
            ],
          },
        },
      },
      {
        stepNumber: 2,
        title: 'Create Your "Mini-Problem"',
        explanation:
          'Replace the scary number with the smallest nice number (like 1, 2, or 3). Ask "What if there were only 2 people?" or "What if it was a 2×2 grid?"',
        tips: [
          "Start with the smallest number that makes sense",
          "Often start at n=1, n=2, or sometimes n=0",
          "If n=1 doesn't make sense (like 1 person handshaking), start at n=2",
          "You'll solve several mini-problems, not just one!",
        ],
        examples: {
          simple: {
            problem: "50 people handshaking → Try 2 people",
            solution: "1 handshake",
            explanation:
              "With 2 people (A and B), there is exactly 1 handshake (A-B).",
          },
          intermediate: {
            problem: "100-floor tower → Try 1, 2, 3, 4 floors",
            solution: "Floor 1→5, Floor 2→8, Floor 3→11, Floor 4→14",
            explanation: "Make tiny towers to see the pattern clearly",
            intermediateSteps: [
              {
                label: "1 floor",
                answer: "5 blocks",
                hint: "Given in problem",
              },
              {
                label: "2 floors",
                answer: "5 + 8 = 13 total",
                hint: "8 = 5+3",
              },
              {
                label: "3 floors",
                answer: "5 + 8 + 11 = 24",
                hint: "11 = 8+3",
              },
              {
                label: "4 floors",
                answer: "5 + 8 + 11 + 14 = 38",
                hint: "14 = 11+3",
              },
            ],
          },
        },
      },
      {
        stepNumber: 3,
        title: "Solve a Few Mini-Problems",
        explanation:
          "Solve for n=1, n=2, n=3, n=4 (or more). Don't stop at one! You need a list of answers to find the pattern.",
        tips: [
          "Make a table to organize your results",
          "Draw diagrams for small cases - they help!",
          "Write down BOTH n and the answer",
          "Do at least 4-5 cases to see the pattern clearly",
        ],
        practice: {
          problem:
            "In a room with 50 people, everyone shakes hands once. How many handshakes?",
          question:
            "First, solve the mini-problems. How many handshakes for small groups?",
          answer: "1, 3, 6, 10",
          hint: "Draw it! With 2 people: 1 handshake. With 3: A-B, A-C, B-C = 3. With 4: previous 3 plus 3 more.",
          intermediateSteps: [
            {
              label: "n=2 people",
              answer: "1",
              hint: "Just A-B = 1 handshake",
            },
            {
              label: "n=3 people",
              answer: "3",
              hint: "A-B, A-C, B-C = 3 handshakes",
            },
            {
              label: "n=4 people",
              answer: "6",
              hint: "Previous 3, plus D shakes with A,B,C = 3+3 = 6",
            },
            {
              label: "n=5 people",
              answer: "10",
              hint: "Previous 6, plus E shakes with A,B,C,D = 6+4 = 10",
            },
          ],
        },
        examples: {
          simple: {
            problem: "Handshakes: Try n=2, 3, 4, 5 people",
            solution: "n=2→1, n=3→3, n=4→6, n=5→10",
            explanation:
              "Draw each case! n=3: A-B, A-C, B-C = 3 handshakes. n=4: 3+2+1 = 6",
            intermediateSteps: [
              { label: "n=2", answer: "1 handshake", hint: "Just A-B" },
              { label: "n=3", answer: "3 handshakes", hint: "A-B, A-C, B-C" },
              {
                label: "n=4",
                answer: "6 handshakes",
                hint: "Previous 3, plus 3 more from D",
              },
              {
                label: "n=5",
                answer: "10 handshakes",
                hint: "Previous 6, plus 4 more from E",
              },
            ],
          },
          intermediate: {
            problem: "Tower blocks: Total for n=1, 2, 3, 4 floors",
            solution: "n=1→5, n=2→13, n=3→24, n=4→38",
            explanation:
              "Each floor adds 3 more blocks than the previous floor",
            intermediateSteps: [
              { label: "n=1", answer: "5", hint: "Floor 1: 5 blocks" },
              { label: "n=2", answer: "13", hint: "5 + 8 = 13" },
              { label: "n=3", answer: "24", hint: "5 + 8 + 11 = 24" },
              { label: "n=4", answer: "38", hint: "5 + 8 + 11 + 14 = 38" },
            ],
          },
        },
      },
      {
        stepNumber: 4,
        title: "Find the Pattern in Your Answers!",
        explanation:
          "Look at your list of answers. Now it's just a \"Find a Pattern\" problem! Identify if it's arithmetic, geometric, triangular, or another pattern.",
        tips: [
          "Look for differences between consecutive answers",
          "Check if differences are constant (arithmetic)",
          "Check if ratios are constant (geometric)",
          "Recognize special patterns: 1,3,6,10 are triangular numbers",
          "Use the pattern to create a formula, then apply to scary number",
        ],
        practice: {
          problem:
            "Pattern in handshakes: 1, 3, 6, 10... What formula works for 50 people?",
          question: "Analyze the pattern and apply the formula:",
          answer: "1225",
          hint: "These are triangular numbers. Formula: n(n-1)/2. For 50 people: 50×49/2",
          intermediateSteps: [
            {
              label: "Pattern type",
              answer: "triangular",
              hint: "Differences +2, +3, +4 (growing)",
            },
            {
              label: "Formula",
              answer: "n(n-1)/2",
              hint: "Standard triangular number formula",
            },
            { label: "50 × 49", answer: "2450", hint: "Multiply first" },
            {
              label: "2450 ÷ 2",
              answer: "1225",
              hint: "Final answer for 50 people!",
            },
          ],
        },
        examples: {
          simple: {
            problem: "Pattern in 1, 3, 6, 10 handshakes?",
            solution: "Triangular numbers! Formula: n(n-1)/2",
            explanation:
              "Differences: +2, +3, +4, +5 (growing). These are triangular numbers.",
            intermediateSteps: [
              {
                label: "Answers",
                answer: "1, 3, 6, 10",
                hint: "Our mini-problem results",
              },
              {
                label: "Differences",
                answer: "+2, +3, +4",
                hint: "Growing by 1 each time",
              },
              {
                label: "Pattern",
                answer: "Triangular",
                hint: "1+2+3+...+(n-1)",
              },
              { label: "Formula", answer: "n(n-1)/2", hint: "Works for any n" },
              {
                label: "For n=50",
                answer: "50×49/2 = 1225",
                hint: "Final answer!",
              },
            ],
          },
          intermediate: {
            problem: "Pattern in 5, 13, 24, 38 tower blocks?",
            solution: "Arithmetic series starting at 5 with difference 3",
            explanation:
              "Each adds 3 more: 5, 8, 11, 14... Sum = n×(first+last)/2",
            intermediateSteps: [
              {
                label: "Floor blocks",
                answer: "5, 8, 11, 14...",
                hint: "Arithmetic: +3 each time",
              },
              {
                label: "nth floor",
                answer: "5 + 3(n-1)",
                hint: "General formula for one floor",
              },
              {
                label: "Total blocks",
                answer: "Sum 1 to n",
                hint: "Add all floors together",
              },
              {
                label: "For n=100",
                answer: "Calculate sum",
                hint: "Use arithmetic series formula",
              },
            ],
          },
        },
      },
    ],
    finalExample: {
      problem:
        "In a room with 50 people, everyone shakes hands with everyone else exactly once. How many total handshakes?",
      fullSolution:
        "1225 (Mini: n=2→1, n=3→3, n=4→6, n=5→10. Pattern: triangular! Formula: n(n-1)/2. For 50: 50×49/2 = 1225)",
    },
  },

  "3c4d5e6f-7a8b-9c0d-1e2f-3a4b5c6d7e8f": {
    title: "Draw a Diagram",
    description:
      "Turn confusing word problems into simple pictures to organize information and see the solution.",
    steps: [
      {
        stepNumber: 1,
        title: 'Read and Find the "Things"',
        explanation:
          "Read the problem once and identify the key objects or actors. What is this problem about?",
        tips: [
          "Look for people (Tom, Sue, Sarah)",
          "Look for places (store, school, well, fence)",
          "Look for parts (fractions, groups, distances)",
          "These are what you'll draw",
        ],
        examples: {
          simple: {
            problem: "A frog is at the bottom of a 10-foot well...",
            solution: "Things: frog, well (10 feet tall)",
            explanation:
              "The key objects are the frog and the well with its height.",
          },
          intermediate: {
            problem:
              "You have a 100-foot fence and want posts every 10 feet...",
            solution: "Things: fence (100 feet), posts (every 10 feet)",
            explanation:
              "The fence is the main object, posts are placed along it.",
            intermediateSteps: [
              { label: "Main object", answer: "fence", hint: "100 feet long" },
              {
                label: "What to place",
                answer: "posts",
                hint: "Every 10 feet",
              },
            ],
          },
        },
      },
      {
        stepNumber: 2,
        title: "Choose Your Diagram Type",
        explanation:
          "Pick the simplest type of drawing for your problem. Don't draw a masterpiece - a simple sketch works best!",
        tips: [
          "Places/directions → Map or line",
          "Fractions/percentages → Bar model or pie chart",
          "Groups/overlaps → Venn diagram (overlapping circles)",
          "Movement up/down → Vertical line with marks",
          "Timeline → Horizontal line with marks",
        ],
        examples: {
          simple: {
            problem: "Frog climbing up a well",
            solution: "Vertical line (up/down movement)",
            explanation:
              "Draw a vertical line for the well, mark heights 0 to 10.",
          },
          intermediate: {
            problem: "Fence posts problem",
            solution: "Horizontal line with marks",
            explanation:
              "Draw a horizontal line for the fence, mark every 10 feet.",
            intermediateSteps: [
              {
                label: "Draw",
                answer: "horizontal line",
                hint: "Represents the 100-foot fence",
              },
              {
                label: "Mark",
                answer: "0, 10, 20, 30...100",
                hint: "Every 10 feet",
              },
            ],
          },
        },
      },
      {
        stepNumber: 3,
        title: "Draw and Label Everything",
        explanation:
          "This is the most important step! Every number, name, or fact in the problem must be added to your drawing. A drawing without labels is just a doodle.",
        tips: [
          "Start drawing your chosen diagram type",
          "Read the problem again, sentence by sentence",
          "Every time you see a number, add it to the drawing",
          "Every time you see a name or fact, label it",
          "Be neat enough to read, but don't waste time on art!",
        ],
        examples: {
          simple: {
            problem: "Frog well: climbs 3 ft daily, slips 2 ft nightly",
            solution: "Draw vertical line, track each day/night",
            explanation:
              "Start at 0. Day 1: up to 3, night: down to 1. Day 2: up to 4, night: down to 2...",
            intermediateSteps: [
              { label: "Start", answer: "0 feet", hint: "Bottom of well" },
              {
                label: "End Day 1",
                answer: "3 - 2 = 1 ft",
                hint: "Climbed 3, slipped 2",
              },
              {
                label: "End Day 2",
                answer: "4 - 2 = 2 ft",
                hint: "From 1, climbed 3, slipped 2",
              },
              {
                label: "Continue",
                answer: "Track each day",
                hint: "Until frog reaches 10",
              },
            ],
          },
          intermediate: {
            problem:
              "Two people 60 miles apart walk toward each other at 3 and 4 mph",
            solution: "Draw line with both people, label speeds and distance",
            explanation:
              "Draw 60-mile line. Mark Person A on left (3 mph), Person B on right (4 mph).",
            intermediateSteps: [
              {
                label: "Draw line",
                answer: "60 miles long",
                hint: "Distance between them",
              },
              {
                label: "Left end",
                answer: "Person A → 3 mph",
                hint: "Moving right",
              },
              {
                label: "Right end",
                answer: "Person B → 4 mph",
                hint: "Moving left",
              },
              {
                label: "Combined",
                answer: "3 + 4 = 7 mph",
                hint: "They approach at 7 mph",
              },
            ],
          },
        },
      },
      {
        stepNumber: 4,
        title: "Use Your Labeled Diagram",
        explanation:
          'Look at your drawing. The problem is no longer a jumble of words - it\'s a picture with clear relationships. The answer often "pops out" or the next step becomes obvious.',
        tips: [
          "Step back and look at the complete picture",
          "What do you need to find? Look for it in the diagram",
          "Count things, measure distances, or calculate from labels",
          'The diagram prevents "off by one" errors',
          "If stuck, the diagram often reveals what calculation to do next",
        ],
        practice: {
          problem:
            "You have a 100-foot fence. You place a post every 10 feet. How many posts do you need?",
          question:
            "Draw a horizontal line and mark every 10 feet from 0 to 100. Then count the marks:",
          answer: "11",
          hint: "Draw marks at: 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100. Count them!",
          intermediateSteps: [
            { label: "First post", answer: "0 feet", hint: "Start of fence" },
            { label: "Last post", answer: "100 feet", hint: "End of fence" },
            {
              label: "All marks",
              answer: "0,10,20,30,40,50,60,70,80,90,100",
              hint: "Every 10 feet",
            },
            {
              label: "Count marks",
              answer: "11",
              hint: "NOT 10! Both ends count",
            },
          ],
        },
        examples: {
          simple: {
            problem: "When does the frog escape the 10-foot well?",
            solution: "Day 8",
            explanation:
              "Track on diagram: End of Day 7 at 7 ft. Day 8: climbs 3 to reach 10 ft (OUT!). Doesn't slip back.",
            intermediateSteps: [
              {
                label: "Day 7 end",
                answer: "7 feet",
                hint: "Net gain of 1 ft per day",
              },
              {
                label: "Day 8 climb",
                answer: "7 + 3 = 10",
                hint: "Reaches top!",
              },
              { label: "Answer", answer: "8 days", hint: "Escapes on day 8" },
            ],
          },
          intermediate: {
            problem: "Count posts on 100-foot fence (posts every 10 feet)",
            solution: "11 posts",
            explanation:
              "Diagram shows: 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100. Count the marks: 11!",
            intermediateSteps: [
              {
                label: "Marks on line",
                answer: "0, 10, 20...100",
                hint: "Draw each position",
              },
              {
                label: "Count marks",
                answer: "11 marks",
                hint: "Not 10! Include both ends",
              },
              {
                label: "Answer",
                answer: "11 posts",
                hint: "Diagram prevents the trap!",
              },
            ],
          },
        },
      },
    ],
    finalExample: {
      problem:
        "A frog at bottom of 10-foot well climbs 3 feet daily, slips 2 feet nightly. How many days to escape?",
      fullSolution:
        "8 days (Draw vertical line 0-10. Track: Day 1→3-2=1, Day 2→4-2=2... Day 7→9-2=7, Day 8→10 OUT! Diagram shows 8 days, not 10.)",
    },
  },

  "4d5e6f7a-8b9c-0d1e-2f3a-4b5c6d7e8f9a": {
    title: "Try Extreme Cases",
    description:
      'Test a problem\'s hidden rules using the smallest, largest, or "weirdest" possible values.',
    steps: [
      {
        stepNumber: 1,
        title: 'Find the "Variable"',
        explanation:
          'Read the problem and find what can change. Look for phrases like "any number," "a list," "a triangle," or "a rectangle."',
        tips: [
          "The variable is what you have control over",
          "Common variables: numbers, shapes, lists",
          'Look for "what is the maximum/minimum" - that\'s a clue!',
          'The variable is often described as "any" or "a certain"',
        ],
        examples: {
          simple: {
            problem:
              "You have 40 meters of fence for a rectangular pen. Largest area?",
            solution: "Variable: length and width of rectangle",
            explanation:
              "The shape can change (length and width vary) but perimeter stays 40.",
          },
          intermediate: {
            problem: "Pick 3 numbers from 1 to 20. Can their sum equal 65?",
            solution: "Variable: which 3 numbers you pick",
            explanation: "You control which 3 numbers to choose from 1-20.",
            intermediateSteps: [
              {
                label: "What varies",
                answer: "the 3 numbers chosen",
                hint: "You pick them",
              },
              {
                label: "What's fixed",
                answer: "1 to 20 range, pick 3",
                hint: "Can't change this",
              },
            ],
          },
        },
      },
      {
        stepNumber: 2,
        title: 'Brainstorm the "Extremes"',
        explanation:
          "What are the weirdest or edgiest values for that variable? Think of special cases that break normal patterns.",
        tips: [
          "For numbers: Try 0, 1, negatives, huge numbers (1,000,000)",
          "For lists: Try all numbers the same (5,5,5,5)",
          'For shapes: Try "degenerate" (flat triangle) or perfect symmetry (square)',
          'Think: "What\'s the smallest? Largest? Weirdest? Most balanced?"',
        ],
        examples: {
          simple: {
            problem: "Rectangle with perimeter 40",
            solution: "Extremes: very skinny (19×1), balanced (10×10)",
            explanation:
              "Skinny: almost a line. Balanced: perfect square. Also try in-between like 15×5.",
            intermediateSteps: [
              {
                label: "Extreme skinny",
                answer: "19 × 1",
                hint: "Almost a line",
              },
              {
                label: "In-between",
                answer: "15 × 5",
                hint: "Moderate rectangle",
              },
              {
                label: "Extreme balanced",
                answer: "10 × 10",
                hint: "Perfect square",
              },
            ],
          },
          intermediate: {
            problem: "Pick 3 numbers from 1-20 to maximize sum",
            solution: "Extreme: pick the 3 largest (18, 19, 20)",
            explanation:
              "To get maximum, pick the extreme (largest possible numbers).",
            intermediateSteps: [
              {
                label: "Strategy",
                answer: "pick largest",
                hint: "For maximum sum",
              },
              {
                label: "Extreme case",
                answer: "18 + 19 + 20",
                hint: "Top 3 numbers",
              },
            ],
          },
        },
      },
      {
        stepNumber: 3,
        title: "Test Your Extreme Cases",
        explanation:
          "Plug the weird values into the problem. Calculate what happens. Often the problem becomes incredibly simple, or it breaks entirely (which teaches you a rule!).",
        tips: [
          "Actually do the math for each extreme case",
          "If it breaks (like dividing by zero), that tells you a boundary!",
          "Compare results from different extremes",
          "Look for which extreme gives max/min values",
        ],
        practice: {
          problem: "Pick 3 numbers from 1 to 10. Can their sum equal 30?",
          question: "Test the extreme case (largest possible sum):",
          answer: "No",
          hint: "Pick the 3 largest numbers (10, 9, 8) and see if they reach 30.",
          intermediateSteps: [
            {
              label: "Extreme case",
              answer: "10, 9, 8",
              hint: "The 3 largest numbers available",
            },
            { label: "Sum them", answer: "27", hint: "10 + 9 + 8 = 27" },
            {
              label: "Compare to 30",
              answer: "27 < 30",
              hint: "Even the best case falls short",
            },
            {
              label: "Conclusion",
              answer: "No",
              hint: "Impossible to reach 30!",
            },
          ],
        },
        examples: {
          simple: {
            problem: "Test rectangle areas (perimeter 40)",
            solution: "19×1=19, 15×5=75, 10×10=100",
            explanation:
              "Calculate area for each: skinny gives 19, balanced gives 100.",
            intermediateSteps: [
              { label: "19×1", answer: "19 m²", hint: "Very small!" },
              { label: "15×5", answer: "75 m²", hint: "Better" },
              { label: "10×10", answer: "100 m²", hint: "Largest so far!" },
            ],
          },
          intermediate: {
            problem: "Can 3 numbers from 1-20 sum to 65?",
            solution: "Maximum possible: 18+19+20=57",
            explanation:
              "Test the extreme (largest 3). If even that doesn't reach 65, it's impossible!",
            intermediateSteps: [
              {
                label: "Largest 3",
                answer: "20 + 19 + 18",
                hint: "Best possible",
              },
              { label: "Sum", answer: "57", hint: "Still less than 65" },
              {
                label: "Conclusion",
                answer: "Impossible",
                hint: "Can't reach 65!",
              },
            ],
          },
        },
      },
      {
        stepNumber: 4,
        title: "Analyze the Results",
        explanation:
          "Look at what happened with your extreme cases. What pattern emerges? What did you learn about what controls the answer?",
        tips: [
          "Compare all your test cases",
          "Identify which extreme gave max/min",
          'Look for patterns: "more balanced = bigger", "all same = simplest"',
          "Use your discovery to answer the original question",
          "Symmetry often gives optimal values",
        ],
        examples: {
          simple: {
            problem: "Pattern from rectangle tests?",
            solution: "More balanced → larger area. Square is optimal.",
            explanation:
              "As rectangles get more balanced (closer to square), area increases. 10×10 square gives maximum.",
            intermediateSteps: [
              {
                label: "Pattern",
                answer: "Balanced is better",
                hint: "Closer to square = bigger area",
              },
              {
                label: "Optimal",
                answer: "Perfect square",
                hint: "10×10 = 100 m²",
              },
              {
                label: "Answer",
                answer: "100 m²",
                hint: "Largest possible area",
              },
            ],
          },
          intermediate: {
            problem: "What did we learn about the sum?",
            solution: "Maximum possible sum is 57, so 65 is impossible",
            explanation:
              "Testing the extreme case (largest 3) revealed the boundary: can't exceed 57.",
            intermediateSteps: [
              {
                label: "Discovery",
                answer: "Max sum is 57",
                hint: "From 20+19+18",
              },
              { label: "Target", answer: "65", hint: "What we need" },
              {
                label: "Conclusion",
                answer: "No, impossible",
                hint: "57 < 65",
              },
            ],
          },
        },
      },
    ],
    finalExample: {
      problem:
        "You have 40 meters of fence for a rectangular pen. What is the largest possible area?",
      fullSolution:
        "100 m² (Test extremes: 19×1=19m², 15×5=75m², 10×10=100m². Pattern: more balanced = bigger. Square (10×10) gives maximum.)",
    },
  },

  "5e6f7a8b-9c0d-1e2f-3a4b-5c6d7e8f9a0b": {
    title: "The Pigeonhole Principle",
    description:
      "If you have more pigeons than pigeonholes, at least one hole must contain more than one pigeon.",
    steps: [
      {
        stepNumber: 1,
        title: 'Identify the "Pigeons"',
        explanation:
          'Find the "things" you are counting or sorting in the problem. These are your pigeons.',
        tips: [
          "Pigeons are the items being placed or sorted",
          "Common pigeons: students, people, socks, items",
          "Look for how many things total",
          "The number of pigeons is usually clearly stated",
        ],
        examples: {
          simple: {
            problem: "In a school with 367 students...",
            solution: "Pigeons: 367 students",
            explanation: "The students are the things being sorted (N = 367).",
          },
          intermediate: {
            problem: "You pull socks from a drawer with 3 colors...",
            solution: "Pigeons: the socks you pull out",
            explanation:
              "The socks are what you're sorting. Number varies based on question.",
            intermediateSteps: [
              {
                label: "What are we sorting",
                answer: "socks",
                hint: "These are the pigeons",
              },
              {
                label: "How many",
                answer: "depends on question",
                hint: "May need to find this!",
              },
            ],
          },
        },
      },
      {
        stepNumber: 2,
        title: 'Identify the "Pigeonholes"',
        explanation:
          "Find the categories or boxes the things are sorted into. This is often NOT stated directly - you have to figure it out!",
        tips: [
          "Holes are the categories pigeons go into",
          "For birthdays → 366 possible days (including leap day)",
          "For birth months → 12 months",
          "For sock colors → number of different colors",
          "For days of week → 7 days",
          "This is the trickiest step!",
        ],
        examples: {
          simple: {
            problem: "367 students... share the same birthday",
            solution: "Pigeonholes: 366 possible birthdays",
            explanation:
              "The categories are the 366 days of the year (k = 366).",
            intermediateSteps: [
              {
                label: "What are categories",
                answer: "birthdays",
                hint: "Days of the year",
              },
              { label: "How many", answer: "366", hint: "365 + leap day" },
            ],
          },
          intermediate: {
            problem: "Sock drawer with red, blue, yellow socks",
            solution: "Pigeonholes: 3 colors",
            explanation: "The categories are the 3 sock colors (k = 3).",
            intermediateSteps: [
              {
                label: "Categories",
                answer: "colors",
                hint: "What makes socks different",
              },
              {
                label: "Count them",
                answer: "3 colors",
                hint: "red, blue, yellow",
              },
            ],
          },
        },
      },
      {
        stepNumber: 3,
        title: "Compare the Numbers",
        explanation:
          "Count your pigeons (N) and your pigeonholes (k). The key question: Do you have more pigeons than holes? (Is N > k?)",
        tips: [
          "Write down N (number of pigeons)",
          "Write down k (number of holes)",
          "Compare: is N > k?",
          "If N > k, you can GUARANTEE sharing",
          'For "minimum number" problems, think worst-case scenario',
        ],
        practice: {
          problem:
            "A drawer has red, blue, and yellow socks. How many socks must you pull to guarantee a matching pair?",
          question: "Use the Pigeonhole Principle to find the minimum:",
          answer: "4",
          hint: "Worst case: pull 1 of each color (3 socks, no pair). The 4th MUST match one!",
          intermediateSteps: [
            {
              label: "Pigeonholes (colors)",
              answer: "3",
              hint: "red, blue, yellow",
            },
            {
              label: "Worst case scenario",
              answer: "3 socks",
              hint: "One of each color, no pair",
            },
            {
              label: "Next sock (4th)",
              answer: "must match",
              hint: "4 socks > 3 colors",
            },
            {
              label: "Answer",
              answer: "4",
              hint: "Minimum to guarantee a pair",
            },
          ],
        },
        examples: {
          simple: {
            problem: "367 students, 366 possible birthdays",
            solution: "N = 367, k = 366. Since 367 > 366, YES!",
            explanation:
              "More students than possible birthdays, so guaranteed sharing.",
            intermediateSteps: [
              {
                label: "Pigeons (N)",
                answer: "367",
                hint: "Number of students",
              },
              {
                label: "Holes (k)",
                answer: "366",
                hint: "Number of birthdays",
              },
              {
                label: "Compare",
                answer: "367 > 366",
                hint: "More pigeons than holes!",
              },
            ],
          },
          intermediate: {
            problem: "How many socks to guarantee a pair? (3 colors)",
            solution: "Worst case: 3 (one of each). Need 4 total.",
            explanation:
              "Worst luck: 1 red, 1 blue, 1 yellow (3 socks, no pair). 4th MUST match!",
            intermediateSteps: [
              {
                label: "Holes (colors)",
                answer: "3",
                hint: "red, blue, yellow",
              },
              {
                label: "Worst case",
                answer: "1 of each = 3 socks",
                hint: "No pair yet",
              },
              {
                label: "4th sock",
                answer: "MUST match one",
                hint: "4 > 3 holes",
              },
              {
                label: "Answer",
                answer: "4 socks",
                hint: "Minimum to guarantee pair",
              },
            ],
          },
        },
      },
      {
        stepNumber: 4,
        title: "Form the Conclusion",
        explanation:
          'If N > k, you can guarantee at least one hole is shared. For "how many in one hole," use the formula ⌈N/k⌉ (N divided by k, rounded up).',
        tips: [
          "Yes/No guarantee: Just check if N > k",
          "Minimum to guarantee: Add 1 to worst case",
          "How many in one hole: Use ⌈N/k⌉",
          "State your conclusion clearly",
          'Example: "At least 2 must share" or "Need 4 socks"',
        ],
        examples: {
          simple: {
            problem: "Can you guarantee 2 students share a birthday?",
            solution: "Yes, guaranteed!",
            explanation:
              "Since 367 > 366, at least 2 students MUST share a birthday.",
            intermediateSteps: [
              { label: "N > k?", answer: "367 > 366 ✓", hint: "Yes!" },
              {
                label: "Conclusion",
                answer: "Guaranteed",
                hint: "At least 2 share",
              },
            ],
          },
          intermediate: {
            problem: "25 people, 12 months. Min in one month?",
            solution: "At least 3 people share a month",
            explanation:
              "⌈25/12⌉ = ⌈2.08⌉ = 3. At least one month has 3+ birthdays.",
            intermediateSteps: [
              { label: "N", answer: "25 people", hint: "Pigeons" },
              { label: "k", answer: "12 months", hint: "Holes" },
              { label: "Formula", answer: "⌈25/12⌉", hint: "Ceiling function" },
              { label: "Calculate", answer: "⌈2.08⌉ = 3", hint: "Round up" },
              { label: "Answer", answer: "At least 3", hint: "In one month" },
            ],
          },
        },
      },
    ],
    finalExample: {
      problem:
        "In a school with 367 students, can you guarantee at least two students share the same birthday?",
      fullSolution:
        "Yes (Pigeons: 367 students. Holes: 366 possible birthdays. 367 > 366, so GUARANTEED at least 2 share a birthday.)",
    },
  },
};
